import { rollRewardCardIdsWithRng } from "./rewards";
import { SKILLS, SkillId } from "../data/skills";
import { CARDS } from "../data/cards";
import { PerkId } from "../data/perks";
import { CombatEngine, CombatSnapshot } from "./combat";
import { RunState, applyPerk, levelUpSkill, CardId, newRun } from "./runState";
import { buildEncounterEnemies } from "./encounterEnemies";
import type { CombatEvent } from "./combatEvents";
import {
  ActionPolicy,
  ActionPolicyContext,
  BalanceEvent,
  createDeterministicRng,
  DEFAULT_MAX_TURNS,
  emptySkillMap,
  SimulationOutcome,
} from "./simulationHelpers";
import { selectFloorTheme } from "../data/floorConfig";
import { generateFloorEncounters } from "./encounterGeneration";
import { createSeededRng } from "../utils/rng";

// Re-export for consumers
export type { ActionPolicy, ActionPolicyContext, BalanceEvent, SimulationOutcome };

const pickLowestHpTarget = (snapshot: CombatSnapshot): number | undefined => {
  let chosen: { hp: number; index: number } | undefined;
  snapshot.enemies.forEach((enemy, index) => {
    if (enemy.hp <= 0) {
      return;
    }
    if (!chosen || enemy.hp < chosen.hp) {
      chosen = { hp: enemy.hp, index };
    }
  });
  return chosen?.index;
};

export const deterministicPolicy: ActionPolicy = ({ snapshot, options }) => {
  // Note: "heal" skill no longer exists, removed heal check
  // Heal less aggressively to reduce grind and heal dominance

  const targetIndex = pickLowestHpTarget(snapshot);
  const skillIds = Object.keys(snapshot.run.player.skills);
  const optionsByPriority = [...options].sort((a, b) => {
    // Prefer target skills; if equal, favor later-acquired skills first
    const aPower = SKILLS[a.skillId]?.requiresTarget ? 2 : 1;
    const bPower = SKILLS[b.skillId]?.requiresTarget ? 2 : 1;
    if (bPower !== aPower) return bPower - aPower;
    // Later-acquired skills appear at higher indices in keys array, so reverse sort
    const aIndex = skillIds.indexOf(a.skillId);
    const bIndex = skillIds.indexOf(b.skillId);
    return bIndex - aIndex;
  });

  const chosen = optionsByPriority.find((option) => {
    if (option.requiresTarget && targetIndex === undefined) {
      return false;
    }
    return true;
  });

  if (chosen) {
    return { kind: "useSkill", skillId: chosen.skillId, targetIndex };
  }

  return { kind: "useSkill", skillId: options[0]?.skillId ?? "attack", targetIndex };
};

export type EncounterMetrics = {
  encounterNumber: number; // 1-6 within the floor
  floorNumber: number; // 1-3
  outcome: SimulationOutcome;
  turns: number;
  victory: boolean;
  defeat: boolean;
  damageDealt: number;
  damageTaken: number;
  healingDone: number;
  shieldingDone: number;
  skillUsage: Record<SkillId, number>;
  skillValue: Record<SkillId, number>;
  rewardsPicked: CardId[];
  actions: EncounterAction[];
  endingState: EncounterEndingState;
  hpManagement: {
    startingHp: number;
    startingMaxHp: number;
    endingHp: number;
    endingMaxHp: number;
    lowestHp: number;
    lowestHpPercent: number;
    endingHpPercent: number;
    nearDeathTurns: number; // Turns spent below 30% HP
  };
};

export type FloorMetrics = {
  floorNumber: number;
  floorTheme: string; // e.g., "Bat Cave", "Goblin Warren"
  encounters: EncounterMetrics[];
  totalEncounters: number;
  clearedEncounters: number;
  floorOutcome: "completed" | "defeated"; // Did they clear all encounters on this floor?
  // Aggregated stats for the entire floor
  totalTurns: number;
  totalDamageDealt: number;
  totalDamageTaken: number;
  totalHealingDone: number;
  avgEncounterTurns: number;
  overallSkillUsage: Record<SkillId, number>;
  overallSkillValue: Record<SkillId, number>;
  hpManagement: {
    startingHp: number; // HP at start of first encounter
    startingMaxHp: number;
    endingHp: number; // HP at end of last encounter
    endingMaxHp: number;
    lowestHp: number; // Lowest HP across all encounters
    lowestHpPercent: number;
    endingHpPercent: number;
    totalNearDeathTurns: number; // Total turns near death across all encounters
  };
};

export type MultiEncounterRun = {
  seed: number;
  floors: FloorMetrics[];
  finalOutcome: "completed" | "defeated";
  totalFloors: number;
};

export type MultiEncounterAggregates = {
  totalRuns: number;
  completionRate: number;
  averageFloorsReached: number;
  perFloorMetrics: Record<
    number,
    {
      winRate: number;
      lossRate: number;
      averageTurns: number | null;
      damagePerTurn: { dealt: number; taken: number; healing: number; shielding: number };
      skillDamageShare: Record<SkillId, number>;
    }
  >;
  overallSkillUsage: Record<SkillId, number>;
  overallSkillDamageShare: Record<SkillId, number>;
};

export type MultiEncounterAnomaly = {
  id: string;
  severity: "warn" | "fail";
  message: string;
  floor?: number;
  details?: Record<string, string | number>;
};

export type EncounterAction = {
  turn: number;
  actor: "player" | "enemy";
  description: string;
};

export type EncounterEndingState = {
  player: {
    hp: number;
    maxHp: number;
    skills: Record<SkillId, number>;
    perks: PerkId[];
  };
  enemies: {
    name: string;
    hp: number;
    maxHp: number;
    atk: number;
  }[];
};

const describePlayerEvent = (
  event: CombatEvent,
  actionSkillId: SkillId | undefined,
  snapshot: CombatSnapshot
): string => {
  const skillName = actionSkillId ? (SKILLS[actionSkillId]?.name ?? actionSkillId) : "Action";
  switch (event.kind) {
    case "playerAttack": {
      const targetName = snapshot.enemies[event.targetIndex]?.name ?? "Enemy";
      return `${skillName} hit ${targetName} for ${event.amount}`;
    }
    case "playerDefend":
      return `${skillName} raised guard (${event.armor} armor)`;
    case "playerHeal":
      return `${skillName} healed for ${event.amount}`;
    case "playerSelfDamage":
      return `${skillName} self-damaged for ${event.amount}`;
    default:
      return `${skillName} resolved`;
  }
};

const describeEnemyEvent = (event: CombatEvent, snapshot: CombatSnapshot): string => {
  if (event.kind !== "enemyAttack") {
    return "Enemy acted";
  }
  const attackerName = snapshot.enemies[event.attackerIndex]?.name ?? "Enemy";
  return `${attackerName} attacked for ${event.amount}`;
};

/**
 * Summarize events from a floor.
 * Note: shieldingDone will be 0 until shield mechanics are implemented.
 */
const summarizeFloorEvents = (
  events: BalanceEvent[]
): {
  damageDealt: number;
  damageTaken: number;
  healingDone: number;
  shieldingDone: number;
  skillUsage: Record<SkillId, number>;
  skillValue: Record<SkillId, number>;
} => {
  let damageDealt = 0;
  let damageTaken = 0;
  let healingDone = 0;
  let shieldingDone = 0;
  const skillUsage = emptySkillMap();
  const skillValue = emptySkillMap();

  for (const entry of events) {
    if (entry.skillId) {
      skillUsage[entry.skillId] += 1;
    }

    switch (entry.event.kind) {
      case "playerAttack":
        damageDealt += entry.event.amount;
        if (entry.skillId) {
          skillValue[entry.skillId] += entry.event.amount;
        }
        break;
      case "enemyAttack":
        damageTaken += entry.event.amount;
        break;
      case "playerDefend":
        shieldingDone += entry.event.armor;
        if (entry.skillId) {
          skillValue[entry.skillId] += entry.event.armor;
        }
        break;
      case "playerHeal":
        healingDone += entry.event.amount;
        if (entry.skillId) {
          skillValue[entry.skillId] += entry.event.amount;
        }
        break;
      case "playerSelfDamage":
        damageTaken += entry.event.amount;
        if (entry.skillId) {
          skillValue[entry.skillId] -= entry.event.amount;
        }
        break;
      default:
        break;
    }
  }

  return {
    damageDealt,
    damageTaken,
    healingDone,
    shieldingDone,
    skillUsage,
    skillValue,
  };
};

const determineFloorOutcome = (
  victory: boolean,
  defeat: boolean,
  reachedTurnLimit: boolean
): SimulationOutcome => {
  if (victory) {
    return "win";
  }
  if (defeat) {
    return "loss";
  }
  if (reachedTurnLimit) {
    return "stalemate";
  }
  return "stalemate";
};

/**
 * Simulate a single encounter (one combat).
 */
const simulateEncounter = (
  run: RunState,
  floorNumber: number,
  encounterNumber: number,
  policy: ActionPolicy,
  seed: number,
  maxTurns: number
): {
  events: BalanceEvent[];
  victory: boolean;
  defeat: boolean;
  turns: number;
  finalRun: RunState;
  finalSnapshot: CombatSnapshot;
  actions: EncounterAction[];
  hpTracking: {
    startingHp: number;
    startingMaxHp: number;
    lowestHp: number;
    nearDeathTurns: number;
  };
} => {
  // Use shared enemy group and scaling logic
  const enemies = buildEncounterEnemies({ floor: floorNumber });

  const engine = new CombatEngine({ run, enemies, seed });
  const policyRng = createDeterministicRng(seed ^ 0xabc123);
  const events: BalanceEvent[] = [];
  const actions: EncounterAction[] = [];
  let turnNumber = 1;

  // Track HP throughout combat
  const startingHp = run.player.hp;
  const startingMaxHp = run.player.maxHp;
  let lowestHp = startingHp;
  let nearDeathTurns = 0;

  while (!engine.isCombatOver()) {
    if (turnNumber > maxTurns) {
      break;
    }

    if (engine.getSnapshot().turn === "player") {
      const snapshot = engine.getSnapshot();
      const options = engine.getAvailableActions();
      const action = policy({
        snapshot,
        options,
        turnNumber,
        seed,
        rng: policyRng,
      });
      const result = engine.takePlayerAction(action);
      for (const event of result.events) {
        actions.push({
          turn: turnNumber,
          actor: "player",
          description: describePlayerEvent(event, action.skillId, result.snapshot),
        });
        events.push({
          seed,
          turn: turnNumber,
          actor: "player",
          skillId: action.skillId,
          event,
        });
      }

      if (result.snapshot.victory || result.snapshot.defeat) {
        break;
      }
    }

    const enemyResult = engine.takeEnemyTurn();
    for (const event of enemyResult.events) {
      actions.push({
        turn: turnNumber,
        actor: "enemy",
        description: describeEnemyEvent(event, enemyResult.snapshot),
      });
      events.push({ seed, turn: turnNumber, actor: "enemy", event });
    }

    if (enemyResult.snapshot.victory || enemyResult.snapshot.defeat) {
      break;
    }

    // Track HP for analysis
    const currentHp = enemyResult.snapshot.run.player.hp;
    const currentMaxHp = enemyResult.snapshot.run.player.maxHp;
    if (currentHp < lowestHp) {
      lowestHp = currentHp;
    }
    // Check if below 30% HP (near death)
    if (currentHp / currentMaxHp < 0.3) {
      nearDeathTurns += 1;
    }

    turnNumber += 1;
  }

  const snapshot = engine.getSnapshot();
  const finalRun = engine.getRunState();
  return {
    events,
    victory: snapshot.victory,
    defeat: snapshot.defeat,
    turns: turnNumber,
    finalRun,
    finalSnapshot: snapshot,
    actions,
    hpTracking: {
      startingHp,
      startingMaxHp,
      lowestHp,
      nearDeathTurns,
    },
  };
};

/**
 * Apply a randomly selected reward to the run state.
 */
const applyRandomReward = (run: RunState, rng: () => number): CardId | null => {
  const choices = rollRewardCardIdsWithRng(run, rng);
  if (choices.length === 0) {
    return null;
  }

  // Pick one reward at random from the choices
  const selectedIndex = Math.floor(rng() * choices.length);
  const selected = choices[selectedIndex];

  const card = CARDS[selected];
  if (card.kind === "skill" && card.grantsSkill) {
    levelUpSkill(run, card.grantsSkill);
  } else if (card.kind === "perk" && card.id.startsWith("perk_")) {
    applyPerk(run, card.id as PerkId);
  }

  return selected;
};

const updateRunFromSnapshot = (target: RunState, source: RunState): void => {
  target.floor = source.floor;
  target.player.hp = source.player.hp;
  target.player.maxHp = source.player.maxHp;
  target.player.atk = source.player.atk;
  target.player.skills = { ...source.player.skills };
  target.player.perks = [...source.player.perks];
};

/**
 * Simulate a full floor with multiple encounters.
 * Generates encounters based on floor config and runs each one sequentially.
 */
const simulateFloor = (
  run: RunState,
  floorNumber: number,
  policy: ActionPolicy,
  seed: number,
  maxTurns: number,
  rewardRng: () => number
): FloorMetrics => {
  const floorRng = createSeededRng(seed);

  // Select floor theme
  const floorConfig = selectFloorTheme(floorNumber, floorRng);
  const floorTheme = floorConfig.name;

  // Generate encounters for this floor
  const encounterConfigs = generateFloorEncounters(floorConfig, floorNumber, floorRng);
  const totalEncounters = encounterConfigs.length;

  const encounters: EncounterMetrics[] = [];
  let clearedEncounters = 0;
  let floorDefeated = false;

  // Track floor-level HP
  const floorStartHp = run.player.hp;
  const floorStartMaxHp = run.player.maxHp;
  let floorLowestHp = floorStartHp;
  let floorTotalNearDeathTurns = 0;

  // Aggregate floor stats
  const floorSkillUsage = emptySkillMap();
  const floorSkillValue = emptySkillMap();
  let floorTotalTurns = 0;
  let floorTotalDamageDealt = 0;
  let floorTotalDamageTaken = 0;
  let floorTotalHealingDone = 0;

  // Run each encounter
  for (let i = 0; i < encounterConfigs.length; i++) {
    const encounterNumber = i + 1;
    const encounterSeed = seed + encounterNumber * 100;

    const { events, victory, defeat, turns, finalRun, finalSnapshot, actions, hpTracking } = simulateEncounter(
      run,
      floorNumber,
      encounterNumber,
      policy,
      encounterSeed,
      maxTurns
    );

    updateRunFromSnapshot(run, finalRun);

    const totals = summarizeFloorEvents(events);

    // After victory, determine and apply rewards
    const rewardsPicked: CardId[] = [];
    if (victory) {
      const reward = applyRandomReward(run, rewardRng);
      if (reward) {
        rewardsPicked.push(reward);
      }
      clearedEncounters++;
    }

    const endingHp = run.player.hp;
    const endingMaxHp = run.player.maxHp;

    // Track floor-level HP
    if (hpTracking.lowestHp < floorLowestHp) {
      floorLowestHp = hpTracking.lowestHp;
    }
    floorTotalNearDeathTurns += hpTracking.nearDeathTurns;

    // Aggregate floor stats
    floorTotalTurns += turns;
    floorTotalDamageDealt += totals.damageDealt;
    floorTotalDamageTaken += totals.damageTaken;
    floorTotalHealingDone += totals.healingDone;
    for (const skillId of Object.keys(SKILLS) as SkillId[]) {
      floorSkillUsage[skillId] += totals.skillUsage[skillId] ?? 0;
      floorSkillValue[skillId] += totals.skillValue[skillId] ?? 0;
    }

    const encounterMetric: EncounterMetrics = {
      encounterNumber,
      floorNumber,
      outcome: determineFloorOutcome(victory, defeat, turns > maxTurns),
      turns,
      victory,
      defeat,
      damageDealt: totals.damageDealt,
      damageTaken: totals.damageTaken,
      healingDone: totals.healingDone,
      shieldingDone: totals.shieldingDone,
      skillUsage: totals.skillUsage,
      skillValue: totals.skillValue,
      rewardsPicked,
      actions,
      endingState: {
        player: {
          hp: run.player.hp,
          maxHp: run.player.maxHp,
          skills: { ...run.player.skills } as Record<SkillId, number>,
          perks: [...run.player.perks],
        },
        enemies: finalSnapshot.enemies.map((enemy) => ({
          name: enemy.name,
          hp: enemy.hp,
          maxHp: enemy.maxHp,
          atk: enemy.atk,
        })),
      },
      hpManagement: {
        startingHp: hpTracking.startingHp,
        startingMaxHp: hpTracking.startingMaxHp,
        endingHp,
        endingMaxHp,
        lowestHp: hpTracking.lowestHp,
        lowestHpPercent: hpTracking.lowestHp / hpTracking.startingMaxHp,
        endingHpPercent: endingHp / endingMaxHp,
        nearDeathTurns: hpTracking.nearDeathTurns,
      },
    };

    encounters.push(encounterMetric);

    // If player is defeated, end the floor
    if (defeat) {
      floorDefeated = true;
      break;
    }
  }

  const floorEndHp = run.player.hp;
  const floorEndMaxHp = run.player.maxHp;

  return {
    floorNumber,
    floorTheme,
    encounters,
    totalEncounters,
    clearedEncounters,
    floorOutcome: floorDefeated ? "defeated" : "completed",
    totalTurns: floorTotalTurns,
    totalDamageDealt: floorTotalDamageDealt,
    totalDamageTaken: floorTotalDamageTaken,
    totalHealingDone: floorTotalHealingDone,
    avgEncounterTurns: encounters.length > 0 ? floorTotalTurns / encounters.length : 0,
    overallSkillUsage: floorSkillUsage,
    overallSkillValue: floorSkillValue,
    hpManagement: {
      startingHp: floorStartHp,
      startingMaxHp: floorStartMaxHp,
      endingHp: floorEndHp,
      endingMaxHp: floorEndMaxHp,
      lowestHp: floorLowestHp,
      lowestHpPercent: floorLowestHp / floorStartMaxHp,
      endingHpPercent: floorEndHp / floorEndMaxHp,
      totalNearDeathTurns: floorTotalNearDeathTurns,
    },
  };
};

/**
 * Run a multi-floor simulation (default 3 floors with multiple encounters each).
 * Each floor has 3-6 encounters based on floor configuration.
 */
export const simulateMultiEncounterRun = (
  totalFloors: number,
  policy: ActionPolicy,
  seed: number,
  maxTurns: number = DEFAULT_MAX_TURNS
): MultiEncounterRun => {
  const run: RunState = newRun();

  const rewardRng = createDeterministicRng(seed ^ 0x123abc);
  const floors: FloorMetrics[] = [];

  // Simulate each floor (typically 3 floors max)
  for (let floorNumber = 1; floorNumber <= totalFloors; floorNumber++) {
    const floorSeed = seed + floorNumber * 1000;

    const floorMetric = simulateFloor(
      run,
      floorNumber,
      policy,
      floorSeed,
      maxTurns,
      rewardRng
    );

    floors.push(floorMetric);

    // If player was defeated on this floor, end the run
    if (floorMetric.floorOutcome === "defeated") {
      break;
    }

    // Advance to next floor
    run.floor += 1;
  }

  return {
    seed,
    floors,
    finalOutcome: floors[floors.length - 1]?.floorOutcome === "defeated" ? "defeated" : "completed",
    totalFloors: floors.length,
  };
};

/**
 * Aggregate metrics from multiple multi-encounter runs.
 */
export const aggregateMultiEncounterRuns = (
  runs: MultiEncounterRun[]
): MultiEncounterAggregates => {
  const totalRuns = runs.length;
  const completedRuns = runs.filter((run) => run.finalOutcome === "completed").length;
  const totalFloorsReached = runs.reduce((sum, run) => sum + run.totalFloors, 0);

  const perFloorMetrics: Record<
    number,
    {
      winRate: number;
      lossRate: number;
      averageTurns: number | null;
      damagePerTurn: { dealt: number; taken: number; healing: number; shielding: number };
      skillDamageShare: Record<string, number>;
    }
  > = {};

  const overallSkillUsage = emptySkillMap();
  const overallSkillValue = emptySkillMap();

  // Find max floor reached across all runs
  const maxFloor = Math.max(...runs.map((run) => run.totalFloors));

  for (let floor = 1; floor <= maxFloor; floor++) {
    const floorData = runs
      .map((run) => run.floors.find((f) => f.floorNumber === floor))
      .filter((f) => f !== undefined);

    if (floorData.length === 0) {
      continue;
    }

    const completed = floorData.filter((f) => f.floorOutcome === "completed").length;
    const defeated = floorData.filter((f) => f.floorOutcome === "defeated").length;
    const totalTurns = floorData.reduce((sum, f) => sum + f.totalTurns, 0);
    const avgTurns = floorData.length > 0 ? totalTurns / floorData.length : null;

    const totalDamageDealt = floorData.reduce((sum, f) => sum + f.totalDamageDealt, 0);
    const totalDamageTaken = floorData.reduce((sum, f) => sum + f.totalDamageTaken, 0);
    const totalHealingDone = floorData.reduce((sum, f) => sum + f.totalHealingDone, 0);
    const totalShieldingDone = 0; // Not tracked at floor level yet

    const skillValueTotals = emptySkillMap();
    for (const f of floorData) {
      for (const skillId of Object.keys(SKILLS) as SkillId[]) {
        skillValueTotals[skillId] += f.overallSkillValue[skillId] ?? 0;
        overallSkillUsage[skillId] += f.overallSkillUsage[skillId] ?? 0;
        overallSkillValue[skillId] += f.overallSkillValue[skillId] ?? 0;
      }
    }

    const totalValue = Object.values(skillValueTotals).reduce((sum, v) => sum + v, 0);
    const skillDamageShare = emptySkillMap();
    for (const skillId of Object.keys(SKILLS) as SkillId[]) {
      skillDamageShare[skillId] = totalValue > 0 ? skillValueTotals[skillId] / totalValue : 0;
    }

    perFloorMetrics[floor] = {
      winRate: floorData.length > 0 ? completed / floorData.length : 0,
      lossRate: floorData.length > 0 ? defeated / floorData.length : 0,
      averageTurns: avgTurns,
      damagePerTurn: {
        dealt: totalTurns > 0 ? totalDamageDealt / totalTurns : 0,
        taken: totalTurns > 0 ? totalDamageTaken / totalTurns : 0,
        healing: totalTurns > 0 ? totalHealingDone / totalTurns : 0,
        shielding: totalTurns > 0 ? totalShieldingDone / totalTurns : 0,
      },
      skillDamageShare,
    };
  }

  const totalOverallValue = Object.values(overallSkillValue).reduce((sum, v) => sum + v, 0);
  const overallSkillDamageShare = emptySkillMap();
  for (const skillId of Object.keys(SKILLS) as SkillId[]) {
    overallSkillDamageShare[skillId] =
      totalOverallValue > 0 ? overallSkillValue[skillId] / totalOverallValue : 0;
  }

  return {
    totalRuns,
    completionRate: totalRuns > 0 ? completedRuns / totalRuns : 0,
    averageFloorsReached: totalRuns > 0 ? totalFloorsReached / totalRuns : 0,
    perFloorMetrics,
    overallSkillUsage,
    overallSkillDamageShare,
  };
};

/**
 * Detect anomalies in multi-encounter runs.
 */
export const detectMultiEncounterAnomalies = (
  aggregates: MultiEncounterAggregates,
  runs: MultiEncounterRun[]
): MultiEncounterAnomaly[] => {
  const anomalies: MultiEncounterAnomaly[] = [];

  const floorNumbers = Object.keys(aggregates.perFloorMetrics)
    .map((f) => parseInt(f, 10))
    .sort((a, b) => a - b);

  // Check each floor for anomalies
  for (const [floorStr, metrics] of Object.entries(aggregates.perFloorMetrics)) {
    const floor = parseInt(floorStr, 10);

    // Win rate too low or too high
    if (metrics.winRate < 0.3) {
      anomalies.push({
        id: `floor-${floor}-low-winrate`,
        severity: "warn",
        message: `Floor ${floor} win rate ${(metrics.winRate * 100).toFixed(1)}% is below 30%`,
        floor,
        details: { winRate: metrics.winRate },
      });
    }
    if (metrics.winRate > 0.95 && floor > 1) {
      anomalies.push({
        id: `floor-${floor}-high-winrate`,
        severity: "warn",
        message: `Floor ${floor} win rate ${(metrics.winRate * 100).toFixed(1)}% is above 95%`,
        floor,
        details: { winRate: metrics.winRate },
      });
    }

    // Average turns too low or too high
    if (metrics.averageTurns !== null) {
      if (metrics.averageTurns < 3) {
        anomalies.push({
          id: `floor-${floor}-turns-too-low`,
          severity: "warn",
          message: `Floor ${floor} average turns ${metrics.averageTurns.toFixed(1)} is below 3 (too swingy)`,
          floor,
          details: { averageTurns: metrics.averageTurns },
        });
      }
      if (metrics.averageTurns > 10) {
        anomalies.push({
          id: `floor-${floor}-turns-too-high`,
          severity: "warn",
          message: `Floor ${floor} average turns ${metrics.averageTurns.toFixed(1)} is above 10 (too grindy)`,
          floor,
          details: { averageTurns: metrics.averageTurns },
        });
      }
    }

    // Skill dominance
    const dominantSkill = Object.entries(metrics.skillDamageShare).reduce(
      (top, [skillId, share]) => (share > top.share ? { skillId, share } : top),
      { skillId: "", share: 0 }
    );
    if (dominantSkill.share > 0.7) {
      anomalies.push({
        id: `floor-${floor}-skill-dominance`,
        severity: "warn",
        message: `Floor ${floor} skill ${dominantSkill.skillId} dominates with ${(dominantSkill.share * 100).toFixed(1)}% share`,
        floor,
        details: { skillId: dominantSkill.skillId, share: dominantSkill.share },
      });
    }

    // HP Risk Analysis - collect HP data from all runs for this floor
    const floorHpData = runs
      .map((run) => run.floors.find((f) => f.floorNumber === floor))
      .filter((f): f is FloorMetrics => f !== undefined && f.floorOutcome === "completed"); // Only analyze completed floors

    if (floorHpData.length > 0) {
      const avgEndingHpPercent =
        floorHpData.reduce((sum, f) => sum + f.hpManagement.endingHpPercent, 0) / floorHpData.length;
      const avgNearDeathTurns =
        floorHpData.reduce((sum, f) => sum + f.hpManagement.totalNearDeathTurns, 0) / floorHpData.length;

      // Too safe: ending with high HP consistently
      if (avgEndingHpPercent > 0.9 && floor >= 2) {
        anomalies.push({
          id: `floor-${floor}-too-safe`,
          severity: "warn",
          message: `Floor ${floor} too safe: average ending HP ${(avgEndingHpPercent * 100).toFixed(0)}% (not threatening enough)`,
          floor,
          details: { avgEndingHpPercent },
        });
      }

      // Too risky: frequently near death
      if (avgNearDeathTurns > 5) {
        anomalies.push({
          id: `floor-${floor}-too-risky`,
          severity: "warn",
          message: `Floor ${floor} too risky: averaging ${avgNearDeathTurns.toFixed(1)} turns near death (<30% HP)`,
          floor,
          details: { avgNearDeathTurns },
        });
      }

      // Vulnerability: ending with low HP (risky for next floor)
      if (avgEndingHpPercent < 0.3 && floor > 1) {
        anomalies.push({
          id: `floor-${floor}-leaves-vulnerable`,
          severity: "warn",
          message: `Floor ${floor} leaves players vulnerable: ${(avgEndingHpPercent * 100).toFixed(0)}% HP remaining on average`,
          floor,
          details: { avgEndingHpPercent },
        });
      }
    }
  }

  // Difficulty Cliff Detection - compare consecutive floors
  for (let i = 1; i < floorNumbers.length; i++) {
    const prevFloor = floorNumbers[i - 1];
    const currFloor = floorNumbers[i];
    const prevMetrics = aggregates.perFloorMetrics[prevFloor];
    const currMetrics = aggregates.perFloorMetrics[currFloor];

    if (prevMetrics && currMetrics) {
      const delta = currMetrics.winRate - prevMetrics.winRate;

      // Difficulty cliff: sharp drop in win rate
      if (delta < -0.4) {
        anomalies.push({
          id: `floor-${currFloor}-difficulty-cliff`,
          severity: "fail",
          message: `Floor ${currFloor} difficulty cliff: win rate dropped from ${(prevMetrics.winRate * 100).toFixed(0)}% to ${(currMetrics.winRate * 100).toFixed(0)}%`,
          floor: currFloor,
          details: { prevWinRate: prevMetrics.winRate, currWinRate: currMetrics.winRate, delta },
        });
      }

      // Unexpected ease: significant increase in win rate on later floors
      if (delta > 0.2 && currFloor > 3) {
        anomalies.push({
          id: `floor-${currFloor}-unexpected-ease`,
          severity: "warn",
          message: `Floor ${currFloor} unexpectedly easier than previous: win rate increased from ${(prevMetrics.winRate * 100).toFixed(0)}% to ${(currMetrics.winRate * 100).toFixed(0)}%`,
          floor: currFloor,
          details: { prevWinRate: prevMetrics.winRate, currWinRate: currMetrics.winRate, delta },
        });
      }
    }
  }

  // Check overall skill dominance across the entire run
  const overallDominantSkill = Object.entries(aggregates.overallSkillDamageShare).reduce(
    (top, [skillId, share]) => (share > top.share ? { skillId, share } : top),
    { skillId: "", share: 0 }
  );
  if (overallDominantSkill.share > 0.7) {
    anomalies.push({
      id: `overall-skill-dominance`,
      severity: "warn",
      message: `Overall skill ${overallDominantSkill.skillId} dominates with ${(overallDominantSkill.share * 100).toFixed(1)}% share across all floors`,
      details: { skillId: overallDominantSkill.skillId, share: overallDominantSkill.share },
    });
  }

  return anomalies;
};
